/**
 * 语文听写页**内部**要挑哪个孩子（纯函数）。
 *
 * ## 为什么需要挑一个孩子
 *
 * 听写的**数据是全家的**（第一步那 6 张表里没有 `student_id`），但后端 21 个听写端点
 * **全部**挂在 `/students/:id/dictation/...`，每个都先走
 * `resolveStudentAccess(app, principal, :id)`（见
 * `apps/api/src/modules/dictation/dictation.routes.ts` 顶部注释）——
 * **没有全局的 `/dictation/*`**。
 *
 * 所以顶层工具页 `/tools/dictation` 的路径里没有 `:id` 之后，仍然必须**在内部选定一个
 * 孩子**塞进那些请求：它只决定
 *   ① 这个家长**能不能进**这一页（`GET /students/:id` 的 `permission`）；
 *   ② **选词预览**默认选中的年级（`pickDictationWords(app, access.student.gradeCode, …)`）。
 * 它**不**决定字表里有什么 —— 换一个孩子看到的是同一份字表。
 *
 * ## ⚠️ 这是内部约定，**界面上不再出现这个概念**（2026-10 修正）
 *
 * 从前这一页有个"换个孩子当锚点"的选择器，还在横幅里解释"权限锚点"。家长的原话是
 * "这一块的「锚点」的作用我不理解" —— 那是**后端技术债泄露到了界面上**：家长为了录
 * 字表，不该先理解"后端接口要挂在某个孩子下面"。
 * 现在：选择器**删掉**了，横幅只说"这套字表是全家人共用的"这种家长用得着的事实；
 * "按谁的年级取词"只在**选词预览**里才有意义，那里直接给**年级**选择器
 * （见 `DictationPickPanel` 的 `defaultGradeCode`）。
 *
 * ## 为什么仍然把选中的孩子放进查询串（`?anchor=`）
 *
 * **只是内部通道，不暴露控件**：将来"从某个孩子的听写布置跳进来"时，那条链接得
 * 把那个孩子的年级带过来当取词默认值 —— 放进 URL 就能做到（刷新/收藏/分享也稳），
 * 放进组件 state 就不能跨页面带。而放进路径（`/tools/dictation/:id`）反而会把
 * "听写是挂在孩子下面的东西"这个印象又带回来 —— 那正是这次重构要去掉的。
 *
 * ## 默认值：**owner 优先、然后按列表顺序**（确定性）
 *
 * 多孩子家庭内部选中的这个孩子必须是**确定的**（同样的数据永远选同一个），否则会出现
 * "这次进来取词默认六年级、下次默认三年级"这种说不清的漂移。
 * 规则（纯函数，有单测）：
 *   1. URL 里的 `?anchor=` 合法（确实是这个家长名下某一员）→ 就用它；
 *   2. 否则取**第一个 owner 的孩子**（自己创建的那个：权限最全，不会是「仅查看」）；
 *   3. 再否则取列表里的第一个（共享家庭里家长自己没创建过孩子）。
 * 一个孩子都没有时返回 `null` —— 页面显示空状态，而不是拿一个空 id 去发请求。
 *
 * ⚠️ "第一个"不是随机的，但也**不是**按名字排的：候选来自 `GET /students`，
 *   后端给的顺序是 `asc(viaShare), desc(createdAt)`（见
 *   `apps/api/src/modules/students/students.service.ts`）—— 即"我创建的在前、
 *   共享给我的在后"，同组内**新建的在前**。所以默认实际上是
 *   "我最近创建的那个孩子"。（后端若改了排序，这里的行为会跟着变，
 *   但**始终是确定的** —— 不会一会儿一个样。）
 *
 * ## 为什么叫 `?anchor=` 而不是 `?studentId=`
 *
 * `?studentId=` 在本项目里**曾经**是"家长预览孩子端界面"的上下文；那个功能已于
 * 2026-10-16 整体删除（连同 `AppShell` 里透传它的 `navSearch`），所以现在
 * `?studentId=` 在这套前端里**没有任何含义**。但名字仍然刻意分开：这一页内部选中的
 * 孩子是一件**独立的事**（给 21 个听写请求定位锚点），将来若再出现别的
 * "上下文孩子"，也不该与它共用同一个参数名。
 *
 * ## 同一件事的另一半：**选词预览默认选哪个年级**
 *
 * 这个孩子身上唯一会被家长看到的东西，就是"选词预览默认选中哪个年级"
 * （`pickDictationWords(app, access.student.gradeCode, …)`）。界面**只显示年级**，
 * 所以那三条语义（没动过 → 默认年级；全不选 → 不限年级；选了几个 → 就那几个）
 * 也放在本文件里当纯函数（`selectedPickGrades` / `pickGradeCodesForRequest`，有单测），
 * 免得"界面显示的"和"真正发出去的"悄悄不一致。
 */
import { GRADE_CODES } from '@fht/shared';

/** 判定候选只需要这三个字段（`StudentListItem` 满足它，测试里也不用造整条记录）。 */
export interface AnchorCandidate {
  id: string;
  nickname: string;
  permission: string;
}

/** `?anchor=` 里的值是否真的指向这个家长名下的一个孩子。 */
export function isKnownAnchor(candidates: readonly AnchorCandidate[], value: string | null): boolean {
  if (!value) return false;
  return candidates.some((item) => item.id === value);
}

/**
 * 选出这一页**内部**实际要用的孩子 id；一个孩子都没有时返回 `null`。
 *
 * 结果**不上界面**（只用来发那 21 个请求 + 给选词预览定默认年级），
 * 所以改名/换孩子都不会让家长看到"锚点"这个词。
 *
 * @param candidates 家长名下的孩子（`GET /students`，含"共享给我的"，带 `permission`）
 * @param requested  查询串里的 `?anchor=`；不合法/缺失时按上面的规则兜底
 */
export function resolveDictationAnchor(
  candidates: readonly AnchorCandidate[],
  requested: string | null,
): AnchorCandidate | null {
  if (candidates.length === 0) return null;
  const asked = candidates.find((item) => item.id === requested);
  if (asked) return asked;
  const owned = candidates.find((item) => item.permission === 'owner');
  return owned ?? candidates[0] ?? null;
}

/**
 * 选词预览里**界面上高亮**的年级。
 *
 * `choice === null` = 家长还没动过选择器 → 用默认年级（内部那个孩子身上的）；
 * 默认年级还不知道（才进页面 / 档案没拿到）→ 空，界面上一个都不高亮，
 * 并提示"全不选 = 不限年级"。
 */
export function selectedPickGrades(
  choice: readonly string[] | null,
  defaultGradeCode: string,
): string[] {
  if (choice !== null) return [...choice];
  return defaultGradeCode === '' ? [] : [defaultGradeCode];
}

/**
 * 选词预览**真正发给接口**的年级（`POST /students/:id/dictation/words` 的 `gradeCodes`）。
 *
 * ⚠️ 两条必须守住：
 *   1. **永远显式传**（不返回空数组）：空了就会被后端当成"没传"而回落到默认年级 ——
 *      界面显示"没选年级"、实际却按某个年级取词，是最难查的那种不一致。
 *      所以"全不选"= 显式给**全部年级** = 不限年级。
 *   2. 没动过选择器时给**默认年级**；默认年级还不知道时退回"全部年级"
 *      （此时界面上也是一个都没高亮，两边一致）。
 *
 * ⚠️ 后端的 `gradeCodes` 数组上限是 12（`dictationWordQuerySchema`），而 `GRADES`
 *   现在正好 12 个 —— 将来加年级（例如学前班）时要同步抬高那个上限，否则
 *   "全不选"这一支会被 zod 拒。
 */
export function pickGradeCodesForRequest(
  choice: readonly string[] | null,
  defaultGradeCode: string,
): string[] {
  const selected = selectedPickGrades(choice, defaultGradeCode);
  if (selected.length > 0) return selected;
  return [...GRADE_CODES];
}
